import React from "react";
import { useLocation } from "react-router";

// Demo-only error boundary that replaces the shared SplashError screen with
// a friendlier "something broke, try again" panel and — critically — resets
// itself whenever the URL changes. The shared ErrorBoundary in
// libs/react-api-core stays in the error state until a full reload (or
// clearing site data), which makes any single crash look like the whole
// demo died.

type DemoErrorBoundaryProps = {
  children: React.ReactNode;
  resetKey: string;
};

type DemoErrorBoundaryState = {
  error: Error | null;
};

class DemoErrorBoundaryInner extends React.Component<
  DemoErrorBoundaryProps,
  DemoErrorBoundaryState
> {
  state: DemoErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): DemoErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[demo] caught error in DemoErrorBoundary:", error, info);
  }

  componentDidUpdate(prevProps: DemoErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleResetDemo = () => {
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch {
      // private mode — best effort
    }
    window.location.replace(window.location.pathname);
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          padding: "40px 24px",
          fontFamily:
            "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 520,
            background: "#ffffff",
            borderRadius: 16,
            padding: "32px 28px",
            boxShadow: "0 18px 60px -20px rgba(7, 39, 26, 0.32)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: "linear-gradient(135deg, #fb923c 0%, #ef4444 100%)",
              color: "#ffffff",
              fontSize: 26,
              fontWeight: 700,
              display: "grid",
              placeItems: "center",
              margin: "0 auto 20px",
            }}
          >
            !
          </div>
          <h2
            style={{
              margin: "0 0 8px",
              fontSize: 20,
              fontWeight: 700,
              color: "#07271a",
            }}
          >
            Something went wrong in the demo
          </h2>
          <p
            style={{
              margin: "0 0 20px",
              fontSize: 14,
              lineHeight: 1.5,
              color: "#475c50",
            }}
          >
            One of the screens hit an unexpected state. Try again or jump back
            to a working page — your other tabs and data are unaffected.
          </p>
          <details
            style={{
              marginBottom: 20,
              fontSize: 12,
              color: "#7e8b80",
              textAlign: "left",
            }}
          >
            <summary style={{ cursor: "pointer" }}>Technical detail</summary>
            <pre
              style={{
                marginTop: 8,
                padding: 8,
                background: "#f4f6ef",
                borderRadius: 6,
                fontSize: 11,
                overflow: "auto",
                maxHeight: 160,
              }}
            >
              {this.state.error.message}
            </pre>
          </details>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button
              type="button"
              onClick={this.handleRetry}
              style={{
                padding: "11px 20px",
                fontSize: 14,
                fontWeight: 600,
                border: 0,
                borderRadius: 10,
                color: "#ffffff",
                background:
                  "linear-gradient(135deg, #00674a 0%, #014e36 100%)",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                padding: "11px 20px",
                fontSize: 14,
                fontWeight: 600,
                border: "1.5px solid #d3d8c2",
                borderRadius: 10,
                color: "#07271a",
                background: "#ffffff",
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleResetDemo}
              style={{
                padding: "11px 20px",
                fontSize: 14,
                fontWeight: 600,
                border: 0,
                background: "transparent",
                color: "#7e8b80",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Reset demo
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export function DemoErrorBoundary({ children }: { children: React.ReactNode }) {
  // Re-mount the inner boundary when the URL changes — clears the error
  // state automatically when the user navigates away from the broken page.
  const location = useLocation();
  return (
    <DemoErrorBoundaryInner resetKey={location.pathname}>
      {children}
    </DemoErrorBoundaryInner>
  );
}
